import React, { useState } from 'react';
import { useToast } from '../hooks/use-toast';
import { sampleReportAPI } from '../lib/api';

const SampleReportForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    pan: '',
    email: '',
    emailOtp: '',
    whatsapp: ''
  });
  
  // Form states: 'form' | 'otp' | 'success'
  const [step, setStep] = useState('form');
  const [isRegisteredUser, setIsRegisteredUser] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'pan') {
      setFormData({ ...formData, pan: value.toUpperCase().trim() });
    } else if (name === 'email') {
      setFormData({ ...formData, email: value.toLowerCase().trim() });
    } else if (name === 'whatsapp') {
      setFormData({ ...formData, whatsapp: value.replace(/\D/g, '').slice(0, 10) });
    } else if (name === 'emailOtp') {
      setFormData({ ...formData, emailOtp: value.replace(/\D/g, '').slice(0, 6) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validateForm = () => {
    if (!formData.name.match(/^[A-Za-z\s]+$/)) {
      toast({
        title: 'Error',
        description: 'Please enter a valid name (only alphabets allowed)',
        variant: 'destructive'
      });
      return false;
    }

    if (!formData.pan.match(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/)) {
      toast({
        title: 'Error',
        description: 'Invalid PAN format (e.g., ABCDE1234F)',
        variant: 'destructive'
      });
      return false;
    }

    if (!formData.email || !formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast({
        title: 'Error',
        description: 'Please enter a valid email',
        variant: 'destructive'
      });
      return false;
    }

    if (!formData.whatsapp.match(/^[0-9]{10}$/)) {
      toast({
        title: 'Error',
        description: 'Please enter a valid 10-digit WhatsApp number',
        variant: 'destructive'
      });
      return false;
    }

    return true;
  };

  // Main submit handler - handles everything with ONE button
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    
    try {
      // Step 1: Submit lead
      const response = await sampleReportAPI.submitLead({
        name: formData.name,
        pan: formData.pan,
        email: formData.email,
        whatsapp: formData.whatsapp
      });

      // Registered user - report sent directly
      if (response.data?.data?.is_registered_user) {
        setIsRegisteredUser(true);
        setStep('success');
        toast({
          title: 'Report Sent!',
          description: 'Sample report has been sent directly to your email!'
        });
        return;
      }

      // New lead - OTP sent, show OTP screen
      setStep('otp');
      toast({
        title: 'OTP Sent',
        description: 'Please check your email for the verification code'
      });

    } catch (error) {
      const errorCode = error.response?.data?.code;

      if (errorCode === 'USER_EXISTS') {
        // Lead exists, send new OTP
        try {
          await sampleReportAPI.sendOtp({ email: formData.email });
          setStep('otp');
          toast({
            title: 'OTP Sent',
            description: 'A verification code has been sent to your email'
          });
        } catch (otpError) {
          toast({
            title: 'Error',
            description: otpError.response?.data?.message || 'Failed to send OTP',
            variant: 'destructive'
          });
        }
      } else {
        toast({
          title: 'Error',
          description: error.response?.data?.message || 'Something went wrong. Please try again.',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP verification and send report
  const handleVerifyAndSend = async (e) => {
    e.preventDefault();

    if (!formData.emailOtp || formData.emailOtp.length !== 6) {
      toast({
        title: 'Error',
        description: 'Please enter a valid 6-digit OTP',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      // Verify OTP
      await sampleReportAPI.submitOtp({
        email: formData.email,
        otp: parseInt(formData.emailOtp)
      });

      // OTP verified, send report
      await sampleReportAPI.sendReport({ email: formData.email });
      
      setStep('success');
      toast({
        title: 'Success!',
        description: 'Sample report has been sent to your email!'
      });

    } catch (error) {
      const errorMessage = error.response?.data?.errors?.otp?.[0] || 
                          error.response?.data?.message || 
                          'Invalid OTP. Please try again.';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    setLoading(true);
    try {
      await sampleReportAPI.sendOtp({ email: formData.email });
      toast({
        title: 'OTP Sent',
        description: 'A new verification code has been sent to your email'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to resend OTP. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({ name: '', pan: '', email: '', emailOtp: '', whatsapp: '' });
    setStep('form');
    setIsRegisteredUser(false);
  };

  // Success Screen
  if (step === 'success') {
    return (
      <div className="text-center py-8 space-y-4">
        <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-900">Report Sent Successfully!</h3>
        {isRegisteredUser ? (
          <p className="text-gray-600">
            As a <span className="font-semibold text-green-600">registered user</span>, we've sent the sample report directly to your email.
            <br />
            <span className="text-sm text-gray-500">No verification was needed!</span>
          </p>
        ) : (
          <p className="text-gray-600">
            The sample stock idea research report has been sent to your email.
            <br />
            <span className="text-sm text-gray-500">Please check your inbox (and spam folder).</span>
          </p>
        )}
        <button
          type="button"
          onClick={resetForm}
          className="mt-4 text-[#1b2d4a] hover:underline font-medium"
        >
          Request another report
        </button>
      </div>
    );
  }

  // OTP Verification Screen
  if (step === 'otp') {
    return (
      <div className="space-y-4">
        <div className="text-center mb-4">
          <div className="w-12 h-12 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Verify Your Email</h3>
          <p className="text-sm text-gray-600 mt-1">
            We've sent a 6-digit code to <span className="font-medium">{formData.email}</span>
          </p>
        </div>

        <form onSubmit={handleVerifyAndSend} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-2 text-gray-700">Enter OTP:</label>
            <input
              type="text"
              name="emailOtp"
              value={formData.emailOtp}
              onChange={handleChange}
              placeholder="Enter 6-digit OTP"
              maxLength={6}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-400 text-center text-xl tracking-widest"
              autoFocus
            />
          </div>

          <button
            type="submit"
            disabled={loading || formData.emailOtp.length !== 6}
            className="w-full bg-yellow-400 text-gray-900 py-3 rounded-lg font-bold hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Verifying...' : 'Verify & Send Report'}
          </button>

          <div className="flex justify-between items-center text-sm">
            <button
              type="button"
              onClick={() => setStep('form')}
              className="text-gray-600 hover:text-gray-800"
            >
              ← Change email
            </button>
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={loading}
              className="text-[#1b2d4a] hover:underline disabled:opacity-50"
            >
              Resend OTP
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Main Form
  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700">Name:</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Enter your name"
          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-400"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700">PAN:</label>
        <input
          type="text"
          name="pan"
          value={formData.pan}
          onChange={handleChange}
          placeholder="Enter your PAN"
          maxLength={10}
          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-400 uppercase"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700">Email:</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Enter your email"
          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-400"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700">WhatsApp No.:</label>
        <input
          type="text"
          name="whatsapp"
          value={formData.whatsapp}
          onChange={handleChange}
          placeholder="Enter your WhatsApp number"
          maxLength={10}
          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-400"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-yellow-400 text-gray-900 py-3 rounded-lg font-bold hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Processing...' : 'Send the report'}
      </button>
    </form>
  );
};

export default SampleReportForm;
